import React from 'react';
import { Box } from '@mui/material';
import { tablet, hexA, money, orderCode, minutesSince, timerColor } from './theme';
import { BOARD_PHASES, TERMINAL_PHASES, type Phase } from './phases';
import { TERMINAL_WINDOW_HOURS } from './useLiveOrders';
import { useTicker } from './useTicker';
import type { AdminOrder } from './types';

function isUber(o: AdminOrder) { return /uber/i.test(o.channel || o.source || '') || !!o.uberDirectId; }
function isDelivery(o: AdminOrder) { return !!o.shippingAddressId || !!o.shippingNote || parseFloat(o.totalDeliveryFee) > 0; }
function itemCount(o: AdminOrder) { return (o.lines || []).reduce((n, l) => n + (Number(l.quantity) || 1), 0); }
function custName(o: AdminOrder) { return o.user ? `${o.user.firstName || ''} ${o.user.lastName || ''}`.trim() || 'Customer' : 'Customer'; }

/**
 * Read-only phase board. Ops can open an order to inspect it, but every
 * state transition belongs to the shop tablet — nothing here mutates.
 */
export function KanbanBoard({ byPhase, onCardClick, showStore }: {
  byPhase: Record<Phase, AdminOrder[]>;
  onCardClick: (o: AdminOrder) => void;
  showStore: boolean;
}) {
  // Card ages are derived at render time, so the board needs its own heartbeat
  // to keep them (and their colour thresholds) truthful between fetches.
  useTicker();

  return (
    <Box
      sx={{
        display: 'grid',
        // One column per phase, each free to shrink (minmax(0,...) rather than a 240px
        // floor). The board used to lay columns out at a 240px minimum and scroll
        // sideways once they overflowed, which meant En route and Delivered sat off
        // screen on smaller displays — on a live-orders board the whole point is
        // seeing every phase at once, so the columns now share the width instead.
        gridTemplateColumns: `repeat(${BOARD_PHASES.length}, minmax(0, 1fr))`,
        gap: 1.5,
        alignItems: 'start',
        pb: 1,
      }}
    >
      {BOARD_PHASES.map((def) => {
        const orders = byPhase[def.key] ?? [];
        const terminal = TERMINAL_PHASES.includes(def.key);
        return (
          <Box
            key={def.key}
            sx={{
              bgcolor: tablet.surface,
              border: `1px solid ${tablet.line}`,
              borderRadius: `${tablet.radius}px`,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 200,
              maxHeight: 'calc(100vh - 260px)',
            }}
          >
            {/* Column header */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.75,
                py: 1.5,
                borderBottom: `1px solid ${tablet.line}`,
                position: 'sticky',
                top: 0,
              }}
            >
              <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: def.pin, flexShrink: 0 }} />
              <Box sx={{ fontWeight: 800, color: tablet.ink, flex: 1, minWidth: 0 }}>
                {def.label}
                {/* Say so, rather than letting a trimmed column read as the full picture. */}
                {terminal && (
                  <Box component="span" sx={{ ml: 0.75, fontSize: 11, fontWeight: 600, color: tablet.faint }}>
                    last {TERMINAL_WINDOW_HOURS}h
                  </Box>
                )}
              </Box>
              <Box
                sx={{
                  minWidth: 22,
                  px: 0.75,
                  textAlign: 'center',
                  borderRadius: 999,
                  bgcolor: hexA(def.pin, 0.14),
                  color: def.pin,
                  fontWeight: 800,
                  fontSize: 12,
                }}
              >
                {orders.length}
              </Box>
            </Box>

            {/* Cards */}
            <Box sx={{ p: 1.25, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              {orders.length === 0 ? (
                <Box sx={{ py: 3, textAlign: 'center', color: tablet.faint, fontSize: 13 }}>—</Box>
              ) : (
                orders.map((o) => {
                  const placed = minutesSince(o.createdAt);
                  const items = itemCount(o);
                  return (
                    <Box
                      key={o.id}
                      onClick={() => onCardClick(o)}
                      sx={{
                        bgcolor: tablet.card,
                        border: `1px solid ${tablet.line}`,
                        borderLeft: `3px solid ${def.pin}`,
                        borderRadius: `${tablet.radiusSm}px`,
                        p: 1.25,
                        cursor: 'pointer',
                        transition: 'box-shadow .15s, transform .15s',
                        '&:hover': {
                          boxShadow: `0 4px 14px ${hexA(def.pin, 0.18)}`,
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                        <Box
                          sx={{
                            width: 24, height: 24, borderRadius: '7px',
                            display: 'grid', placeItems: 'center',
                            fontWeight: 700, fontSize: 11, flexShrink: 0,
                            color: isUber(o) ? '#fff' : tablet.mintInk,
                            bgcolor: isUber(o) ? '#111' : tablet.mint,
                          }}
                        >
                          {isUber(o) ? 'U' : 'D'}
                        </Box>
                        <Box sx={{ fontWeight: 800, color: tablet.ink, fontSize: 13 }}>
                          {orderCode(o.number, o.id)}
                        </Box>
                        <Box sx={{ flex: 1 }} />
                        <Box sx={{ fontWeight: 700, fontSize: 12, color: timerColor(placed, terminal) }}>
                          {terminal ? '✓' : `${placed}m`}
                        </Box>
                      </Box>

                      <Box
                        sx={{
                          fontWeight: 700,
                          color: tablet.ink,
                          fontSize: 13,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {custName(o)}
                      </Box>

                      {showStore && (
                        <Box
                          sx={{
                            fontSize: 11,
                            color: tablet.faint,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {o.store?.name || o.storeId}
                        </Box>
                      )}

                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.75 }}>
                        <Box sx={{ fontSize: 11, color: tablet.faint, flex: 1 }}>
                          {isDelivery(o) ? 'Delivery' : 'Collection'} · {items} item{items !== 1 ? 's' : ''}
                        </Box>
                        <Box sx={{ fontWeight: 800, color: tablet.ink, fontSize: 13 }}>
                          {money(parseFloat(o.total) || 0)}
                        </Box>
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
